import { css } from '@emotion/react';
import React, { Fragment, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import t from 'react-translate';
import { Button } from 'react-bootstrap';
import ClickableContainer from 'components/clickable-container';
import NvFlyoutModal, { ModalType } from 'shared/components/nv-flyout-modal';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvSelect, { SelectOption } from 'shared/components/inputs/nv-select';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { SectionSubsection } from 'redux/schemas/models/learner-progress';
import { gray7, info, white } from 'styles/global_defaults/colors';
import { textMediumFontSize } from 'styles/global_defaults/fonts';
import { halfSpacing, largeSpacing, standardSpacing, threeQuartersSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import { screenXsMax } from 'styles/global_defaults/media-queries';

export type LearnerProgressFilters = {
  inactiveForEnabled: boolean;
  inactiveFor: number | null;
  assignmentsEnabled: boolean;
  assignmentId: number | null;
  assignmentName: string | null;
};

export const DEFAULT_FILTERS: LearnerProgressFilters = {
  inactiveForEnabled: false,
  inactiveFor: null,
  assignmentsEnabled: false,
  assignmentId: null,
  assignmentName: null,
};

type FiltersFlyoutModalProps = {
  showModal: boolean;
  onClose: () => void;
  onApply: (filters: LearnerProgressFilters) => void;
  initialFilters?: LearnerProgressFilters;
  isEditMode?: boolean;
};

type FiltersFlyoutFormProps = {
  onClose: () => void;
  onApply: (filters: LearnerProgressFilters) => void;
  initialFilters: LearnerProgressFilters;
  activities: { id: number; title: string }[];
  isEditMode?: boolean;
};

const styles = css`
  &:focus {
    outline: none !important;
  }

  .header {
    height: ${tripleSpacing}px;
    position: sticky;
    top: 0;
    background: ${white};
    z-index: 10;
    margin: 0;
  }

  .filters-actions {
    position: sticky;
    top: ${tripleSpacing}px;
    background: ${white};
    z-index: 10;
  }

  .filter-row {
    background-color: ${gray7};

    &.active {
      background-color: ${info};
    }
    border-radius: ${halfSpacing}px;
    padding: ${threeQuartersSpacing}px ${standardSpacing}px;
    margin-bottom: ${threeQuartersSpacing}px;

    .filter-checkbox-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: ${halfSpacing}px;

      .nv-checkbox-label {
        font-size: ${textMediumFontSize}px;
      }
    }

    .inactive-days-input {
      width: 70px;

      .bs4-input-group {
        height: 30px;
      }

      .bs4-form-control {
        text-align: center;
        height: 30px;
      }
    }

    .assignment-dropdown {
      flex: 1;
      min-width: 150px;

      @media (max-width: ${screenXsMax}px) {
        flex-basis: 100%;
      }
    }
    .assignment-dropdown-select {
      width: 100%;
      position: relative;

      .text-medium {
        height: 45px;
        background-color: ${white};
      }
      div {
        width: 100%;
      }
      .select-menu {
        max-height: 200px;
        overflow-y: auto;
        width: 100% !important;
        transform: none !important;
        top: 100% !important;
        left: 0 !important;

        > div {
          height: auto;
          min-height: ${largeSpacing}px;
          padding: ${halfSpacing}px ${threeQuartersSpacing}px;
        }
      }
    }
  }
`;

const FiltersFlyoutForm = ({
  onClose,
  onApply,
  initialFilters,
  activities,
  isEditMode = false,
}: FiltersFlyoutFormProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { watch, setValue, handleSubmit, reset } = useForm<LearnerProgressFilters>({
    mode: 'onChange',
    defaultValues: initialFilters,
  });

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const formValues = watch();

  const hasFormChanged = () => {
    const { inactiveForEnabled, inactiveFor, assignmentsEnabled, assignmentId } = formValues;
    return (
      inactiveForEnabled !== initialFilters.inactiveForEnabled
      || inactiveFor !== initialFilters.inactiveFor
      || assignmentsEnabled !== initialFilters.assignmentsEnabled
      || assignmentId !== initialFilters.assignmentId
    );
  };

  const isApplyDisabled = () => {
    const { inactiveForEnabled, inactiveFor, assignmentsEnabled, assignmentId } = formValues;

    if (!inactiveForEnabled && !assignmentsEnabled) return true;
    if (inactiveForEnabled && (!inactiveFor || inactiveFor < 1)) return true;
    if (assignmentsEnabled && !assignmentId) return true;
    if (isEditMode && !hasFormChanged()) return true;

    return false;
  };

  const resetFilters = () => {
    reset(DEFAULT_FILTERS);
  };

  const onSubmit = (data: LearnerProgressFilters) => {
    onApply(data);
    onClose();
  };

  const selectOptions: SelectOption[] = activities.map((activity) => ({
    value: activity.id,
    label: activity.title,
  }));

  return (
    <div css={styles} ref={containerRef} tabIndex={-1} role='region' aria-label={t.LEARNER_PROGRESS.FILTERS.TITLE()}>
      <h2 className='header d-flex justify-content-center align-items-center text-large-regular font-weight-bolder'>
        {t.LEARNER_PROGRESS.FILTERS.TITLE()}
      </h2>
      <div className={`filters-actions d-flex align-items-center pt-2 pb-4 px-4 ${isEditMode ? 'justify-content-end' : 'justify-content-between'}`}>
        {!isEditMode && (
          <ClickableContainer
            onClick={resetFilters}
            className='text-primary'
            data-qa='filters-reset-button'
          >
            {t.LEARNER_PROGRESS.FILTERS.RESET_FILTERS()}
          </ClickableContainer>
        )}
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isApplyDisabled()}
          data-qa='filters-apply-button'
        >
          {isEditMode ? t.FORM.UPDATE() : t.LEARNER_PROGRESS.FILTERS.APPLY_FILTERS()}
        </Button>
      </div>
      <div className='px-4 pb-3 text-regular'>
        {t.LEARNER_PROGRESS.FILTERS.DESCRIPTION()}
      </div>
      <div className='px-4'>
        <div className={`filter-row${formValues.inactiveForEnabled ? ' active' : ''}`}>
          <div className='filter-checkbox-row'>
            <NvCheckbox
              name='inactiveForEnabled'
              label={formValues.inactiveForEnabled
                ? t.LEARNER_PROGRESS.FILTERS.INACTIVE_FOR_MORE_THAN()
                : t.LEARNER_PROGRESS.FILTERS.INACTIVE_FOR_MORE_THAN_X_DAYS()}
              checked={formValues.inactiveForEnabled}
              onChange={(e) => setValue('inactiveForEnabled', e.target.checked)}
              dataQa='filters-inactive-days-checkbox'
            />
            {formValues.inactiveForEnabled && (
              <Fragment>
                <NvTextInput
                  type='number'
                  min={1}
                  required
                  className='inactive-days-input'
                  value={formValues.inactiveFor ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : null;
                    setValue('inactiveFor', val);
                  }}
                  ariaLabel={t.LEARNER_PROGRESS.FILTERS.DAYS()}
                />
                <span className='text-regular'>{t.LEARNER_PROGRESS.FILTERS.DAYS()}</span>
              </Fragment>
            )}
          </div>
        </div>
        <div className={`filter-row${formValues.assignmentsEnabled ? ' active' : ''}`}>
          <div className='filter-checkbox-row'>
            <NvCheckbox
              name='assignmentsEnabled'
              label={formValues.assignmentsEnabled
                ? t.LEARNER_PROGRESS.FILTERS.HAS_NOT_COMPLETED()
                : t.LEARNER_PROGRESS.FILTERS.HAS_NOT_COMPLETED_SPECIFIC()}
              checked={formValues.assignmentsEnabled}
              onChange={(e) => setValue('assignmentsEnabled', e.target.checked)}
              dataQa='filters-not-completed-checkbox'
            />
            {formValues.assignmentsEnabled && (
              <div className='assignment-dropdown'>
                <NvSelect
                  options={selectOptions}
                  className='assignment-dropdown-select'
                  value={formValues.assignmentId}
                  placeholder={t.LEARNER_PROGRESS.FILTERS.PLEASE_SELECT()}
                  ariaLabel={t.LEARNER_PROGRESS.FILTERS.HAS_NOT_COMPLETED()}
                  onChange={(value) => {
                    const activity = activities.find((a) => a.id === value);
                    setValue('assignmentId', value);
                    setValue('assignmentName', activity?.title ?? null);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FiltersFlyoutModal = ({
  showModal,
  onClose,
  onApply,
  initialFilters,
  isEditMode = false,
}: FiltersFlyoutModalProps) => {
  const allSections: SectionSubsection[] = useSelector((state) => state.models.courseOutlineSections);

  const activities = useMemo(() => {
    const result: { id: number; title: string }[] = [];

    if (!allSections) return result;

    allSections.forEach((section) => {
      if (section.lecturePages) {
        section.lecturePages.forEach((page) => {
          result.push({ id: page.id, title: page.title });
        });
      }
      if (section.lectureSubsections) {
        section.lectureSubsections.forEach((subsection) => {
          if (subsection.lecturePages) {
            subsection.lecturePages.forEach((page) => {
              result.push({ id: page.id, title: page.title });
            });
          }
        });
      }
    });

    return result;
  }, [allSections]);

  const mobileStyles = css`
    .flyout-modal .flyout-content {
      min-width: min(600px, 100vw);
      width: min(600px, 100vw);
    }
  `;

  return (
    <Fragment>
      {showModal && (
        <div css={mobileStyles}>
          <NvFlyoutModal
            type={ModalType.RIGHT}
            header='Filters'
            width={600}
            onClose={onClose}
            closeDataQa='filters-flyout-close-button'
          >
            <FiltersFlyoutForm
              onClose={onClose}
              onApply={onApply}
              initialFilters={initialFilters ?? DEFAULT_FILTERS}
              activities={activities}
              isEditMode={isEditMode}
            />
          </NvFlyoutModal>
        </div>
      )}
    </Fragment>
  );
};

export default FiltersFlyoutModal;
